import type { EstimateDraft, PriceSearchPrefill } from '../api/constructionPriceEstimatorFunctions';

export const ACTIVE_JOB_STATUSES = new Set(['pending', 'queued', 'processing', 'running']);

export const emptyDraft = (): EstimateDraft => ({
  draft_revision: 0,
  project_location: { country: 'US', state: '', county: '', city: '', postal_code: '' },
  estimate_date: new Date().toISOString().slice(0, 10),
  construction_type: '',
  davis_bacon_construction_type: null,
  size_drivers: [],
  work_items: [],
  project_assumptions: [],
  exclusions: [],
  adjustments: [],
  required_acknowledgements: [],
});

export const partialPrefillDraft = (prefill?: PriceSearchPrefill | null): EstimateDraft | null => {
  if (prefill?.status !== 'current' || !prefill.partial_draft) return null;
  const partial = prefill.partial_draft;
  const empty = emptyDraft();
  return {
    ...empty, ...partial, draft_revision: partial.draft_revision ?? 0,
    content_hash: undefined, construction_type: partial.construction_type ?? '',
    project_location: { ...empty.project_location, ...partial.project_location },
  };
};

export const mergeUnsavedDraftFields = (
  accepted: EstimateDraft, baseline: EstimateDraft, local: EstimateDraft,
): EstimateDraft => {
  const changed = <K extends keyof EstimateDraft>(key: K): boolean => (
    JSON.stringify(local[key]) !== JSON.stringify(baseline[key])
  );
  const location = { ...accepted.project_location };
  Object.entries(local.project_location).forEach(([key, value]) => {
    if (JSON.stringify(value) !== JSON.stringify(baseline.project_location[key])) location[key] = value;
  });
  const choose = <K extends keyof EstimateDraft>(key: K): EstimateDraft[K] => (
    changed(key) ? local[key] : accepted[key]
  );
  return {
    ...accepted,
    // The accepted hash belongs to the server snapshot, not this merged
    // editor text. A later save obtains a hash for the owner's actual draft.
    content_hash: undefined,
    project_location: location,
    approximate_cost_plan_settings: choose('approximate_cost_plan_settings'),
    estimate_date: choose('estimate_date'),
    construction_type: choose('construction_type'),
    price_search_terms: choose('price_search_terms'),
    davis_bacon_construction_type: choose('davis_bacon_construction_type'),
    size_drivers: choose('size_drivers'),
    work_items: choose('work_items'),
    project_assumptions: choose('project_assumptions'),
    exclusions: choose('exclusions'),
    adjustments: choose('adjustments'),
    required_acknowledgements: choose('required_acknowledgements'),
    comparable_reviews: choose('comparable_reviews'),
    timing_requirements: choose('timing_requirements'),
  };
};

export function nestedObject<T>(value: unknown, key: string): T | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const nested = record[key];
  return nested && typeof nested === 'object' ? nested as T : null;
}

export function draftContent(value: EstimateDraft): string {
  return JSON.stringify({
    approximate_cost_plan_settings: value.approximate_cost_plan_settings ?? null,
    project_location: value.project_location,
    estimate_date: value.estimate_date,
    construction_type: value.construction_type,
    price_search_terms: value.price_search_terms,
    davis_bacon_construction_type: value.davis_bacon_construction_type ?? null,
    size_drivers: value.size_drivers,
    work_items: value.work_items,
    project_assumptions: value.project_assumptions,
    exclusions: value.exclusions,
    adjustments: value.adjustments,
    required_acknowledgements: value.required_acknowledgements,
    comparable_reviews: value.comparable_reviews ?? [],
    timing_requirements: value.timing_requirements ?? [],
  });
}

export function draftPayload(
  reason: string,
  source: EstimateDraft,
  comparableReviews = source.comparable_reviews,
  expectedRevision = source.draft_revision,
) {
  return {
    expected_draft_revision: expectedRevision,
    change_reason: reason,
    ...(source.approximate_cost_plan_settings !== undefined
      ? { approximate_cost_plan_settings: source.approximate_cost_plan_settings } : {}),
    project_location: source.project_location,
    estimate_date: source.estimate_date,
    construction_type: source.construction_type,
    price_search_terms: source.price_search_terms,
    davis_bacon_construction_type: source.davis_bacon_construction_type ?? null,
    size_drivers: source.size_drivers,
    work_items: source.work_items,
    project_assumptions: source.project_assumptions,
    exclusions: source.exclusions,
    adjustments: source.adjustments,
    required_acknowledgements: source.required_acknowledgements,
    timing_requirements: source.timing_requirements ?? [],
    ...(comparableReviews?.length ? { comparable_reviews: comparableReviews } : {}),
  };
}
